<template>
  <div class="w-full">
    <!-- Main Navbar -->
    <div class="navbar bg-base-100 dark:bg-neutral text-base-content dark:text-neutral-content shadow-md fixed top-0 z-50 transition-all duration-300">
      <div class="container mx-auto">
        <!-- Mobile Nav & Sidebar Button -->
        <div class="flex-none lg:hidden" v-if="menu">
          <button @click="toggleMenu" class="btn btn-square btn-ghost">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-6 h-6 stroke-current">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
        
        <!-- Logo Section -->
        <div class="flex-1">
          <router-link to="/" class="flex items-center gap-2 transition-transform hover:scale-105 duration-300">
            <img :src="logo" alt="logo" class="md:h-12 h-10">
          </router-link>
        </div>

        <div class="hidden lg:flex flex-1 justify-center" v-if="isLoggedIn">
          <ul class="menu menu-horizontal px-1 gap-2">
            <li>
              <router-link to="/users" class="rounded-lg font-medium group" :class="{'active': $route.path === '/users'}">
                <span class="relative">
                  Dashboard
                  <span class="absolute bottom-0 left-0 w-0 group-hover:w-full h-0.5 bg-primary transition-all duration-300"></span>
                </span>
              </router-link>
            </li>

          </ul>
        </div>

        <!-- Actions Section -->
        <div class="flex-none flex items-center gap-2">
          <!-- Theme Toggle -->
          <div>
            <ThemeToggle />
          </div>

          <!-- Notification Dropdown -->
          <div class="dropdown dropdown-end" v-if="isLoggedIn">
            <label tabindex="0" class="btn btn-ghost btn-circle">
              <div class="indicator">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span class="badge badge-xs badge-primary indicator-item"></span>
              </div>
            </label>
            <div tabindex="0" class="dropdown-content menu p-2 shadow-lg bg-base-100 dark:bg-neutral rounded-box w-64 mt-2">
              <div class="p-2 border-b border-base-300 dark:border-neutral-focus flex justify-between items-center">
                <h3 class="font-bold">Notifications</h3>
                <button class="btn btn-xs btn-ghost">Mark all read</button>
              </div>
              <div class="max-h-60 overflow-y-auto">
                <div class="py-2 px-3 hover:bg-base-200 dark:hover:bg-neutral-focus rounded-lg my-1 cursor-pointer">
                  <div class="flex items-start gap-2">
                    <div class="w-2 h-2 rounded-full bg-primary mt-2"></div>
                    <div>
                      <p class="text-sm font-medium">New task assigned</p>
                      <p class="text-xs opacity-70">You have been assigned a new task</p>
                      <p class="text-xs opacity-50 mt-1">5 minutes ago</p>
                    </div>
                  </div>
                </div>
                <div class="py-2 px-3 hover:bg-base-200 dark:hover:bg-neutral-focus rounded-lg my-1 cursor-pointer">
                  <div class="flex items-start gap-2">
                    <div class="w-2 h-2 rounded-full bg-primary mt-2"></div>
                    <div>
                      <p class="text-sm font-medium">Meeting reminder</p>
                      <p class="text-xs opacity-70">Team meeting in 15 minutes</p>
                      <p class="text-xs opacity-50 mt-1">15 minutes ago</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="p-2 border-t border-base-300 dark:border-neutral-focus mt-2">
                <router-link to="/notifications" class="btn btn-ghost btn-xs w-full">View all</router-link>
              </div>
            </div>
          </div>

          <!-- Authentication -->
          <div v-if="!isLoggedIn" class="flex gap-1">
            <router-link to="/users/login" class="btn btn-ghost btn-sm rounded-lg group">
              <UserCircleIcon class="h-5 w-5 mr-1" />
              <span class="hidden sm:inline group-hover:translate-x-1 transition-transform duration-300">Log In</span>
            </router-link>
            <router-link to="/users/signIn" class="btn btn-primary btn-sm rounded-lg transition-all duration-300 hover:shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span class="hidden sm:inline">Sign Up</span>
            </router-link>
          </div>

          <!-- User Dropdown -->
          <div v-if="isLoggedIn" class="dropdown dropdown-end">
            <label tabindex="0" class="btn btn-ghost btn-sm rounded-lg gap-2 pr-2 sm:pr-4 cursor-pointer">
              <div class="avatar placeholder online">
                <div class="bg-primary text-primary-content rounded-full w-8">
                  <span>{{ userInitials }}</span>
                </div>
              </div>
              <span class="hidden md:inline truncate max-w-[120px]">{{ userData?.name }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </label>
            <ul tabindex="0" class="dropdown-content menu shadow-lg bg-base-100 dark:bg-neutral rounded-box w-64 mt-2 p-2 z-50">
              <li class="menu-title">
                <span>Account</span>
              </li>
              <li>
                <router-link :to="`/users/detail/${userData?.id}`" class="flex items-center p-3 group">
                  <UserCircleIcon class="h-5 w-5 mr-2 group-hover:text-primary transition-colors" />
                  <div>
                    <div class="font-medium group-hover:translate-x-1 transition-transform duration-300">My Profile</div>
                    <div class="text-xs opacity-60">View your profile details</div>
                  </div>
                </router-link>
              </li>
              <li>
                <router-link :to="`/users/update/${userData?.id}`" class="flex items-center p-3 group">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2 group-hover:text-primary transition-colors">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                  </svg>
                  <div>
                    <div class="font-medium group-hover:translate-x-1 transition-transform duration-300">Edit Profile</div>
                    <div class="text-xs opacity-60">Update your information</div>
                  </div>
                </router-link>
              </li>
              <li>
                <router-link to="/options" class="flex items-center p-3 group">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2 group-hover:text-primary transition-colors">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  <div>
                    <div class="font-medium group-hover:translate-x-1 transition-transform duration-300">Settings</div>
                    <div class="text-xs opacity-60">Manage your preferences</div>
                  </div>
                </router-link>
              </li>
              <div class="divider my-1"></div>
              <li>
                <a @click="logOutFNC" class="flex items-center p-3 text-error hover:bg-error/10 group">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                  </svg>
                  <div>
                    <div class="font-medium group-hover:translate-x-1 transition-transform duration-300">Sign Out</div>
                    <div class="text-xs opacity-60">Log out of your account</div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>


    <!-- Search Overlay -->
    <div v-if="showSearch" class="fixed inset-0 bg-black/50 z-[60] flex items-start justify-center pt-20">
      <div class="bg-base-100 dark:bg-neutral w-full max-w-2xl mx-4 rounded-lg shadow-xl p-4 animate-slideDown">
        <div class="flex justify-between items-center mb-4">
          <h3 class="font-bold text-lg">Search</h3>
          <button @click="showSearch = false" class="btn btn-sm btn-circle btn-ghost">✕</button>
        </div>
        <div class="form-control">
          <div class="input-group w-full flex gap-1">
            <input type="text" placeholder="Search..." class="input input-bordered w-full focus:outline-none" ref="searchInput" />
            <button class="btn btn-square btn-primary">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>
        <div class="mt-4">
          <p class="text-sm opacity-70">Quick Links:</p>
          <div class="flex flex-wrap gap-2 mt-2">
            <router-link to="/tasks" class="badge badge-outline p-3">Tasks</router-link>
            <router-link to="/kanban" class="badge badge-outline p-3">Kanban</router-link>
            <router-link to="/calendar" class="badge badge-outline p-3">Calendar</router-link>
            <router-link to="/users" class="badge badge-outline p-3">Users</router-link>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Spacer to prevent content from being hidden under fixed header -->
    <div class="h-16"></div>
  </div>
</template>

<script setup>
import { getCurrentInstance, ref, computed, defineProps, defineEmits, onMounted, nextTick } from "vue";
import ThemeToggle from "../ThemeToggle.vue";
import { UserCircleIcon } from "@heroicons/vue/24/solid";
import { useRouter } from "vue-router";
import useModelUsers from "../../compositions/useModelUsers";
import useFirebaseAuth from "../../compositions/useFirebaseAuth";
import useUrl from "../../compositions/useUrl";


const app = getCurrentInstance();
const store = app.appContext.config.globalProperties.$store;
const props = defineProps(["menu"]);
const emit = defineEmits(['toggle-menu']);
const router = useRouter();
const { logOut } = useModelUsers();
const { url, login } = useUrl();


// Menu toggle functionality
const toggleMenu = () => {
  emit('toggle-menu');
};

const logo = computed(() => {
  let site = import.meta.env.VITE_SITE;
  return `/logo-${site}.png`;
});

// Search functionality
const showSearch = ref(false);
const searchInput = ref(null);

const toggleSearch = () => {
  showSearch.value = !showSearch.value;
  if (showSearch.value) {
    nextTick(() => {
      searchInput.value.focus();
    });
  }
};

// Use Firebase auth composable
const { 
  user,
  isLoggedIn,
  logout
} = useFirebaseAuth();

const currentUser = computed(() => {
  return store.getters._getCurrentUser;
});

// Convert Firebase user to app user format
const userData = computed(() => {
  if (!user.value) return null;
  return {
    id: user.value.uid,
    email: user.value.email,
    name: user.value.displayName || '',
    username: user.value.email?.split('@')[0] || '',
  };
});

const logOutFNC = async () => {
  try {
    await logout();
    store.commit("logoutUser");
    router.push({name: "HomePage"});
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

const userInitials = computed(() => {
  if (!userData.value) return '';
  return (userData.value.name?.charAt(0) || '') + (userData.value.username?.charAt(0) || '');
});
</script>

<style scoped>
.dropdown-content {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .navbar {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
